import { supabase } from './supabase.js';

export const smsSettingsService = {
  // Get SMS settings for a member
  async getSettings(memberId) {
    const { data, error } = await supabase
      .from('sms_settings')
      .select('*')
      .eq('member_id', memberId)
      .single();
    
    if (error && error.code !== 'PGRST116') { // Not found error
      throw error;
    }
    
    // Return default settings if none exist
    if (!data) {
      return {
        member_id: memberId,
        phone_number: null,
        is_enabled: false,
        notification_types: ["daily_reminders", "family_activity_voting", "confirmed_family_plans", "new_idea", "family_time_invite"],
        quiet_hours_start: '22:00',
        quiet_hours_end: '08:00',
        timezone: 'America/New_York'
      };
    }
    
    return data;
  },

  // Update SMS settings
  async updateSettings(memberId, updates) {
    const { data: existing } = await supabase
      .from('sms_settings')
      .select('*')
      .eq('member_id', memberId)
      .single();
    
    if (existing) {
      // Update existing settings
      const { data, error } = await supabase
        .from('sms_settings')
        .update(updates)
        .eq('member_id', memberId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } else {
      // Create new settings
      const { data, error } = await supabase
        .from('sms_settings')
        .insert([{ 
          member_id: memberId, 
          ...updates 
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    }
  },

  // Check if a specific notification type is enabled
  async isNotificationEnabled(memberId, notificationType) {
    const settings = await this.getSettings(memberId);
    
    if (!settings.is_enabled || !settings.phone_number) {
      return false;
    }
    
    return settings.notification_types.includes(notificationType);
  },

  // Get all SMS settings
  async getAllSettings() {
    const { data, error } = await supabase
      .from('sms_settings')
      .select('*');
    
    if (error) throw error;
    return data || [];
  },

  // Get members who have SMS enabled
  async getEnabledMembers() {
    const { data, error } = await supabase
      .from('sms_settings')
      .select('*')
      .eq('is_enabled', true)
      .not('phone_number', 'is', null);
    
    if (error) throw error;
    return data || [];
  },

  // Check if current time is within quiet hours for a member
  isQuietHours(settings, currentTime = new Date()) {
    if (!settings.quiet_hours_start || !settings.quiet_hours_end) {
      return false;
    }

    const [startHour, startMinute] = settings.quiet_hours_start.split(':').map(Number);
    const [endHour, endMinute] = settings.quiet_hours_end.split(':').map(Number);
    
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();
    
    const currentMinutes = currentHour * 60 + currentMinute;
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;
    
    // Handle overnight quiet hours (e.g., 22:00 to 08:00)
    if (startMinutes > endMinutes) {
      return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
    } else {
      return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
    }
  }
};